/**
 * @file US zip code lookup, backed by the platform location queries.
 */

import {
  locationSearch,
  locationData,
} from '../components/global/LocationInput/graphql';

const US_POST_CODE_PATTERN = /^(\d{5})(?:-\d{4})?$/;

/**
 * Reduces a US zip code to the 5 digit form the lookup searches on.
 *
 * @param {string} postCode Raw user input.
 * @returns {string|null} The 5 digit zip, or null when it is not one.
 */
const toLookupablePostCode = postCode => {
  const [, fiveDigits] =
    US_POST_CODE_PATTERN.exec((postCode ?? '').trim()) ?? [];

  return fiveDigits ?? null;
};

/**
 * Resolves a US zip code to its city and state.
 *
 * `locationSearch` is a general place autocomplete, so it happily returns
 * street addresses and business names that merely start with the digits typed
 * (and it biases towards the server's own region). Only a record whose name is
 * the zip code itself is a real postal code match, so anything else is thrown
 * away rather than guessed at. The `, USA` suffix is what pins the search to
 * the US - without it several valid zips return no postal code record at all.
 *
 * Resolves to null whenever the zip does not exist, is outside the US (Puerto
 * Rico and Guam are separate countries here), or the lookup fails, so callers
 * can silently leave the fields for the user to fill in.
 *
 * @param {object} client Apollo client.
 * @param {string} postCode Zip code to look up.
 * @returns {Promise<{ city: string, state: string }|null>} Matched place.
 */
const lookupUsPostCode = async (client, postCode) => {
  const zip = toLookupablePostCode(postCode);

  if (!zip) {
    return null;
  }

  try {
    const { data: searchData } = await client.query({
      query: locationSearch,
      variables: { query: `${zip}, USA` },
    });

    const match = (searchData?.locationSearch?.records ?? []).find(
      record => record.name === zip,
    );

    if (!match) {
      return null;
    }

    const { data: locationDataResult } = await client.query({
      query: locationData,
      variables: { locationId: match.id },
    });

    const location = locationDataResult?.location;

    if (location?.country?.iso !== 'US') {
      return null;
    }

    const state = location.state?.short_name ?? '';

    // A zip that does not exist can still match a place *named* after those
    // digits - "00000" resolves to a street in Far Hills NJ, whose real zip is
    // 07931 - and filling that in would silently give the user a city and
    // state they never asked for. A genuine zip is formatted as its own postal
    // code ("Austin, TX 78701, USA"), so require that before trusting it.
    if (!location.freeform_address?.includes(`${state} ${zip}`)) {
      return null;
    }

    return {
      // Zips covering an unincorporated area come back without a city, but
      // still carry the place name on address_line_1.
      city: location.city || location.address_line_1 || '',
      state,
    };
  } catch (error) {
    return null;
  }
};

export { toLookupablePostCode, lookupUsPostCode };
